/**
 * Theme Color Token Configuration
 *
 * Architecture:
 * 1. BASE PALETTE (API themes): primary-*, secondary-*, neutral-*
 * 2. SEMANTIC TOKENS: purpose-based names → base palette references
 * 3. LEGACY ALIASES: old names → semantic tokens (temporary)
 * 4. TAILWIND COLOR MAP: API tokens → core Tailwind CSS variables
 */

export type BaseColorToken =
  | 'primary-50'
  | 'primary-100'
  | 'primary-200'
  | 'primary-500'
  | 'primary-600'
  | 'primary-700'
  | 'primary-800'
  | 'primary-900'
  | 'primary-950'
  | 'secondary-50'
  | 'secondary-100'
  | 'secondary-500'
  | 'secondary-700'
  | 'neutral-0'
  | 'neutral-50'
  | 'neutral-100'
  | 'neutral-200'
  | 'neutral-300'
  | 'neutral-400'
  | 'neutral-500'
  | 'neutral-600'
  | 'neutral-900'
  | 'overlay-light'
  | 'overlay-medium'
  | 'overlay-dark'
  | 'footer-bg-alpha'
  | 'shadow-text-alpha'
  | 'header-shadow-alpha'
  | 'shadow-card-primary'
  | 'shadow-card-secondary'
  | 'shadow-item-1'
  | 'shadow-item-2';

export type DarkBaseColorToken =
  | BaseColorToken
  | 'secondary-100-alpha'
  | 'neutral-text'
  | 'neutral-700'
  | 'neutral-800';

export const SEMANTIC_TOKEN_MAP: Record<string, BaseColorToken> = {
  'cta-bg': 'secondary-500',
  'cta-shadow': 'secondary-50',
  'cta-hover-bg': 'secondary-500',

  'page-bg': 'primary-50',
  'card-bg': 'primary-50',
  'footer-bg': 'footer-bg-alpha',
  'footer-social-bg': 'primary-600',

  link: 'primary-800',
  'text-primary': 'primary-500',
  'text-secondary': 'neutral-500',
  'text-inactive': 'neutral-600',
  'text-disabled': 'neutral-400',

  'gradient-title-from': 'secondary-100',
  'gradient-title-to': 'secondary-700',
  'gradient-card-from': 'primary-200',
  'gradient-card-to': 'primary-100',

  'card-expanded-bg': 'neutral-50',
  'card-expanded-border': 'neutral-0',
  'card-expanded-text': 'neutral-900',
  'card-item-bg': 'neutral-0',
  'card-icon-bg': 'neutral-100',
  'card-chevron': 'neutral-300',
  'card-chevron-hover': 'neutral-500',
  'card-item-hover-bg': 'secondary-500',
  'card-item-hover-text': 'neutral-0',

  'nav-separator': 'secondary-500',
  'header-bg': 'neutral-0',
  'header-shadow': 'header-shadow-alpha',
  'sidebar-bg': 'primary-50',
  'select-hover': 'primary-50'
};

export const DARK_SEMANTIC_TOKEN_MAP: Record<string, DarkBaseColorToken> = {
  'cta-bg': 'secondary-500',
  'cta-shadow': 'secondary-50',
  'cta-hover-bg': 'secondary-500',

  'page-bg': 'primary-50',
  'card-bg': 'neutral-50',
  'footer-bg': 'footer-bg-alpha',
  'footer-social-bg': 'primary-100',

  link: 'primary-500',
  'text-primary': 'primary-500',
  'text-secondary': 'neutral-500',
  'text-inactive': 'neutral-600',
  'text-disabled': 'neutral-400',

  'gradient-title-from': 'secondary-100-alpha',
  'gradient-title-to': 'primary-500',
  'gradient-card-from': 'primary-700',
  'gradient-card-to': 'primary-500',

  'card-expanded-bg': 'neutral-700',
  'card-expanded-border': 'neutral-800',
  'card-expanded-text': 'neutral-text',
  'card-item-bg': 'neutral-50',
  'card-icon-bg': 'neutral-100',
  'card-chevron': 'neutral-300',
  'card-chevron-hover': 'neutral-500',
  'card-item-hover-bg': 'secondary-500',
  'card-item-hover-text': 'neutral-0',

  'nav-separator': 'neutral-200',
  'header-bg': 'neutral-900',
  'header-shadow': 'header-shadow-alpha',
  'sidebar-bg': 'neutral-800',
  'select-hover': 'neutral-700',
};

/** @deprecated Use semantic tokens directly. Remove after Phase 2 migration. */
export const LEGACY_ALIAS_MAP: Record<string, keyof typeof SEMANTIC_TOKEN_MAP> =
{
  'login-bg': 'page-bg',
  'login-cta': 'cta-bg',
  'login-cta-shadow': 'cta-shadow',
  'login-overlay-light': 'overlay-light',
  'login-overlay-medium': 'overlay-medium',
  'login-text-shadow': 'shadow-text-alpha',

  'landing-header-bg': 'header-bg',
  'landing-header-shadow': 'header-shadow',
  'landing-card-bg': 'card-bg',
  'landing-title-gradient-from': 'gradient-title-from',
  'landing-title-gradient-to': 'gradient-title-to',
  'landing-text-primary': 'text-primary',
  'landing-text-secondary': 'text-secondary',
  'landing-nav-inactive': 'text-inactive',
  'landing-nav-separator': 'nav-separator',
  'landing-bg-overlay': 'overlay-dark',

  'report-card-gradient-from': 'gradient-card-from',
  'report-card-gradient-to': 'gradient-card-to',
  'report-card-bg': 'card-expanded-bg',
  'report-card-border': 'card-expanded-border',
  'report-card-text': 'card-expanded-text',
  'report-card-item-bg': 'card-item-bg',
  'report-card-icon-bg': 'card-icon-bg',
  'report-card-disabled-text': 'text-disabled',
  'report-card-chevron': 'card-chevron',
  'report-card-chevron-hover': 'card-chevron-hover',
  'report-card-shadow-primary': 'shadow-card-primary',
  'report-card-shadow-secondary': 'shadow-card-secondary',
  'report-card-item-shadow-1': 'shadow-item-1',
  'report-card-item-shadow-2': 'shadow-item-2',
  'report-card-item-hover-bg': 'card-item-hover-bg',
  'report-card-item-hover-text': 'card-item-hover-text',
};

/** Maps API customColors tokens → core Tailwind/shadcn CSS variables */
export const TAILWIND_COLOR_MAP: Record<string, BaseColorToken> = {
  background: 'neutral-0',
  foreground: 'neutral-900',
  card: 'neutral-0',
  'card-foreground': 'neutral-900',
  popover: 'neutral-0',
  'popover-foreground': 'neutral-900',
  primary: 'primary-500',
  'primary-foreground': 'neutral-0',
  secondary: 'neutral-100',
  'secondary-foreground': 'neutral-900',
  muted: 'neutral-100',
  'muted-foreground': 'neutral-500',
  accent: 'neutral-100',
  'accent-foreground': 'neutral-900',
  border: 'neutral-200',
  input: 'neutral-200',
  ring: 'primary-700',
};

export const DARK_TAILWIND_COLOR_MAP: Record<string, DarkBaseColorToken> = {
  background: 'neutral-900',
  foreground: 'neutral-text',
  card: 'neutral-800',
  'card-foreground': 'neutral-text',
  popover: 'neutral-800',
  'popover-foreground': 'neutral-text',
  primary: 'primary-500',
  'primary-foreground': 'neutral-0',
  secondary: 'neutral-100',
  'secondary-foreground': 'neutral-text',
  muted: 'neutral-800',
  'muted-foreground': 'neutral-500',
  accent: 'neutral-700',
  'accent-foreground': 'neutral-text',
  border: 'neutral-200',
  input: 'neutral-200',
  ring: 'primary-700',
};
